import React, { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Icon } from "../../../components/Component";
import { Dropdown, Input, Segmented, Spin, Tag, message } from "antd";
import type { MenuProps } from "antd";
import {
  AppstoreOutlined,
  ArrowsAltOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  FileExcelOutlined,
  FilterOutlined,
  LoadingOutlined,
  MoreOutlined,
  PauseCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  ShrinkOutlined,
  SyncOutlined,
  TableOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";

import GanttChart from "../../../components/gantt";
import GridView from "./GridView";
import KanbanView from "./KanbanView";
import { activityApis } from "../../../api/Activity";
import { phaseApis } from "../../../api/Phase";
import { useLocation } from "react-router";
import TopMenu from "../../../route/topMenu";
import { updateMenuData } from "../../../store/slices/misc/menuData";
import { tasksApis } from "../../../api/NewTasks";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { Badge, Drawer, Select, Tooltip, Button as AntButton, Space } from "antd";
import FormMultiSelect from "../../form-components/MultiSelectComponent";
import FormSelect from "../../form-components/SelectComponent";
import { projectsApis } from "../../../api/projects";
import { Empty } from "antd";
import { documentApis } from "../../../api/Documents";
import WbsUploadModal from "./WbsUploadModal";
import TaskTypeUploadModal, { TaskUploadKey } from "./TaskTypeUploadModal";
import ApplyNormUploadModal from "../norms/ApplyNormUploadModal";
import ApplyManpowerNormUploadModal from "../norms/ApplyManpowerNormUploadModal";
import ApplyMachineryNormUploadModal from "../norms/ApplyMachineryNormUploadModal";
import TaskScheduleUploadModal from "./TaskScheduleUploadModal";
import TaskActualUploadModal from "./TaskActualUploadModal";
import Export from "../../../components/export/Export";
import { AddAccess } from "../../../components/accessControl/AccessControl";
import ProjectPageShell from "../shared/ProjectPageShell";
import StatCard from "../shared/StatCard";
import WbsCodeToggle from "../shared/WbsCodeToggle";
import { useWbsName } from "../shared/useWbsCodes";
import { threeMReportApis } from "../../../api/ThreeMReport";

type TaskStatKey = "inprogress" | "completed" | "overdue" | "onhold";

const normStatus = (s: any) =>
  String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

// A task counts as overdue when it isn't finished and its (planned) end date
// is in the past. The tree nodes don't expose a derived Deviated/Delayed
// status, so this is computed from status + endDate.
const isTaskOverdue = (node: any) => {
  const s = normStatus(node?.status);
  if (s === "completed" || s === "closed") return false;
  return node?.endDate
    ? dayjs(node.endDate).isBefore(dayjs(), "day")
    : false;
};

const STAT_PREDICATES: Record<TaskStatKey, (node: any) => boolean> = {
  inprogress: (n) => normStatus(n?.status) === "inprogress",
  completed: (n) => normStatus(n?.status) === "completed",
  overdue: isTaskOverdue,
  onhold: (n) => normStatus(n?.status) === "onhold",
};

const TasksListView = () => {
  const [loading, setLoading] = useState(false);
  const [taskDropdownData, setTaskDropdownData] = useState<any[]>([]);
  // All bulk uploads go through the staged upload modals: WBS has its own
  // wrapper; the four task-level types share the parameterized one.
  const [wbsUploadOpen, setWbsUploadOpen] = useState(false);
  const [taskUploadOpen, setTaskUploadOpen] = useState(false);
  const [applyNormUploadOpen, setApplyNormUploadOpen] = useState(false);
  const [applyManpowerUploadOpen, setApplyManpowerUploadOpen] = useState(false);
  const [applyMachineryUploadOpen, setApplyMachineryUploadOpen] = useState(false);
  const [scheduleUploadOpen, setScheduleUploadOpen] = useState(false);
  const [actualUploadOpen, setActualUploadOpen] = useState(false);
  const [taskUploadKey, setTaskUploadKey] =
    useState<TaskUploadKey>("task-budget");

  const handleBulkUploadMenuClick = (key: string) => {
    if (key === "wbs-import") {
      setWbsUploadOpen(true);
      return;
    }
    setTaskUploadKey(key as TaskUploadKey);
    setTaskUploadOpen(true);
  };

  const location = useLocation();
  const navigate = useNavigate();
  const {
    id: stateProjectId,
    derivedProjectStatus,
    projectName: stateProjectName,
    listViewType: initialListViewType,
    extra: { siteName: projectSiteName, siteId: projectSiteId } = {} as any,
    filterActivityId,
    filterActivityName,
    filterPhaseId,
    filterPhaseName,
    filterStatus,
    fromDashboard,
  } = location.state || {};

  const { data: currentUser } = useSelector((state: any) => state.currentUser);

  // Get projectId from Redux layout state as fallback
  const { currentProjectId, currentProjectName } = useSelector(
    (state: RootState) => state.layout
  );

  // Use Redux projectId as fallback when location state doesn't have it
  const projectId = stateProjectId || currentProjectId;
  const projectName = stateProjectName || currentProjectName;

  const dispatch = useDispatch();
  const methods = useForm({
    mode: "onChange",
  });
  // Only set gantt view if explicitly passed from notification, otherwise default to table
  const [listViewType, setListViewType] = useState(initialListViewType === "gantt" ? "gantt" : "table");
  const [GridData, setGridData] = useState([]);

  // Default to "taskList" if activity filter is present, otherwise "phases"
  const [groupBy, setGroupBy] = useState(() => (filterActivityId ? "taskList" : "phases"));
  const [dateFilter, setDateFilter] = useState<string>("ALL");

  const groupByOptions = [
    {
      key: "taskList",
      label: "Group By Activity",
    },
    ...(listViewType !== "Kanban"
      ? [{ key: "phases", label: "Group By Phases" }]
      : []),
    ...(listViewType === "Kanban"
      ? [{ key: "status", label: "Group By Status" }]
      : []),
  ].filter(Boolean);

  const dateFilterOptions = [
    {
      key: "ALL",
      label: "All Tasks",
    },
    {
      key: "TODAY",
      label: "Today",
    },
    {
      key: "NEXT_7_DAYS",
      label: "Next 7 Days",
    },
    {
      key: "NEXT_14_Days",
      label: "Next 14 Days",
    },
    {
      key: "NEXT_30_Days",
      label: "Next 30 Days",
    },
    {
      key: "OverDue",
      label: "Overdue",
    },
  ];

  // Reset groupBy to valid option when switching views
  useEffect(() => {
    if (listViewType === "Kanban" && groupBy === "phases") {
      setGroupBy("taskList"); // Default to "Group By Activity" for Kanban
    } else if (listViewType !== "Kanban" && groupBy === "status") {
      setGroupBy("taskList"); // Default to "Group By Activity" for non-Kanban views
    }
  }, [listViewType, groupBy]);

  // Initialize appliedFilters with activity filter if coming from activities page
  // Note: activity values are stored as strings in the dropdown
  const [appliedFilters, setAppliedFilters] = useState<any>(() => {
    const init: any = {};
    if (filterActivityId) {
      init.activity = [filterActivityId.toString()];
    }
    if (filterStatus) {
      init.taskStatus = filterStatus;
    }
    return init;
  });
  const [filtersModal, setFiltersModal] = useState(false);
  const [filterCount, setFilterCount] = useState(
    () => (filterActivityId ? 1 : 0) + (filterStatus ? 1 : 0),
  );
  const [activeActivityFilter, setActiveActivityFilter] = useState<{ id: number; name: string } | null>(() => {
    if (filterActivityId && filterActivityName) {
      return { id: filterActivityId, name: filterActivityName };
    }
    return null;
  });
  const [resourceHeadFiltersList, setresourceHeadFiltersList] = useState([]);
  const [tagFilterData, setTagFilterData] = useState([]);
  const [statusFilterData, setStatusFilterData] = useState([]);
  const [activityDropdownData, setActivityDropdownData] = useState<any[]>([]);
  const [assigneeFilterData, setAssigneeFilterData] = useState<any[]>([]);

  // Client-side controls for the table view: quick name search over the tree
  // and the stat-card status filter (both applied over GridData, never mutated).
  const [treeSearch, setTreeSearch] = useState("");
  const [statFilter, setStatFilter] = useState<TaskStatKey | null>(null);
  // Progressive disclosure: phases-only by default, toolbar toggles expand-all
  const [expandedMode, setExpandedMode] = useState<"phases" | "all">("phases");
  // Phase/activity names embed a WBS code (e.g. "HRT - Adits Excavation(HEP-B1.7.1.1)").
  // Hidden by default; the preference is shared with every other PMS screen.
  const wbsName = useWbsName();
  // Option labels are formatted at render, not at fetch, so they re-read the
  // toggle without a refetch.
  const activityFilterOptions = useMemo(
    () =>
      activityDropdownData.map((option: any) => ({
        ...option,
        label: wbsName(option?.label),
      })),
    [activityDropdownData, wbsName]
  );

  // allowAdd for Add Task button (same as Phases/Activities)
  const [allowAdd, setAllowAdd] = useState(true);
  const [activityOptionsWithPhase, setActivityOptionsWithPhase] = useState<
    { label: string; value: string; activityName: string; phaseId: number; phaseName: string }[]
  >([]);

  // Function to handle filter submission
  const handleFilterSubmit = (data: any) => {
    // Filter out empty values (null, undefined, empty string, empty array)
    const filteredData = Object.keys(data).reduce((acc: any, key) => {
      const value = data[key];
      if (
        value !== null &&
        value !== undefined &&
        value !== "" &&
        (!Array.isArray(value) || value.length > 0)
      ) {
        acc[key] = value;
      }
      return acc;
    }, {});

    // Count how many filters are applied
    const count = Object.keys(filteredData).length;

    setFilterCount(count);
    // setFilteredData(filteredData);
    setAppliedFilters(filteredData);
    setFiltersModal(false);

    // Refresh data with filtered data (only non-empty values)
    refresh(true, filteredData);
  };

  // Function to reset filters
  const handleResetFilters = () => {
    methods.reset();
    setAppliedFilters({});
    // setFilteredData({});
    setFilterCount(0);
    setFiltersModal(false);
    setActiveActivityFilter(null);

    // Clear filter from location state
    navigate(location.pathname, {
      state: {
        ...location.state,
        filterActivityId: undefined,
        filterActivityName: undefined,
        filterPhaseId: undefined,
        filterPhaseName: undefined,
      },
      replace: true,
    });

    // Refresh data without filters
    refresh(true, {});
  };

  const fetchGroupByActivityListData = async (
    updateLoading: boolean = true,
    filters: any = appliedFilters
  ) => {
    try {
      if (updateLoading) setLoading(true);

      // Prepare filter parameters for API call
      const filterParams = {
        activityIds: filters.activity ? filters.activity.join(",") : undefined,
        taskIds: filters.task ? filters.task.join(",") : undefined,
        activityOwnerIds: filters.resourceHead
          ? filters.resourceHead.join(",")
          : undefined,
        taskOwnerIds: filters.assignee ? filters.assignee.join(",") : undefined,
        tagIds: filters.tag ? filters.tag.join(",") : undefined,
        completionPercentage: filters.completion || undefined,
        activityStatus: filters.taskStatus || undefined,
        taskDateFilter: dateFilter !== "ALL" ? dateFilter : undefined, // Add date filter parameter
      };

      const apiResponse = await activityApis.fetchGroupByActivityList(
        {
          page: 1,
          pageSize: 100000,
          direction: "P",
          sortOrder: "ASC",
          pageDiff: 0,
          paginationId: null,
          ...filterParams, // Spread the filter parameters
        },
        projectId
      );
      const { data, status } = apiResponse;
      if (status) {
        const { list, count, projectResponse } = data;
        const {
          status: projectStatus,
          isLocked,
          lockedBy,
        } = projectResponse || {};

        let _allowProjectEdit = true;
        if (
          ["closed", "onhold", "archived"]?.includes(
            projectStatus?.toLowerCase()
          )
        )
          _allowProjectEdit = false;
        if (isLocked && lockedBy !== currentUser?.id) {
          _allowProjectEdit = false;
        }
        setAllowAdd(_allowProjectEdit);
        const formatedData = list.map((item: any) => {
          const phaseResponse = {
            id: item?.phaseId,
            name: item?.phaseName,
            status: item?.phaseStatus,
            startDate: item?.phaseStartDate,
            endDate: item?.phaseEndDate,
          };
          let _allowActivityEdit = _allowProjectEdit;
          if (
            ["closed", "onhold", "archived"]?.includes(
              phaseResponse.status?.toLowerCase()
            )
          )
            _allowActivityEdit = false;
          if (
            ["closed", "onhold", "archived"]?.includes(
              item.status?.toLowerCase()
            )
          )
            _allowActivityEdit = false;
          const activityResponse: any = {
            key: "A-" + item.activityId,
            id: item.activityId,
            name: item.activityName,
            owner: item.activityOwnerName,
            status: item?.status,
            updatedBy: item.updatedBy || null,
            updatedByName: item.updatedByName || "",
            createdAt: item.createdAt,
            createdUserName: item.createdUserName,
            startDate: item.activityStartDate,
            endDate: item.activityEndDate,
            duration: null,
            priority: null,
            completionPercentage: item.tasksCompletionPercentage,
            expanded: true,
            manuallyScheduled: true,
            cls: "activity-node",
            children: [],
            projectResponse: { ...projectResponse, name: projectName },
            phaseResponse,
            allowEdit: _allowActivityEdit,
          };

          // Convert each task under this activity
          if (item.tasks) {
            activityResponse.children = item.tasks.map((task: any) => {
              let _allowTaskEdit = _allowActivityEdit;
              if (
                ["closed", "onhold", "archived"]?.includes(
                  task.taskStatus?.toLowerCase()
                )
              )
                _allowTaskEdit = false;
              return {
                key: "T-" + task.taskId,
                id: task.taskId,
                name: task.taskName,
                owner: task.taskOwnerName,
                createdAt: task.createdAt,
                createdUserName: task.createdUserName,
                status: task.taskStatus,
                startDate: task.taskPlannedStartDate,
                endDate: task.taskPlannedEndDate,
                duration: task.taskDuration,
                priority: task.priorityName,
                completionPercentage: task.taskCompletionPercentage,
                manuallyScheduled: true,
                predecessors: task.predecessors,
                successors: task.successors,
                projectResponse: { ...projectResponse, name: projectName },
                phaseResponse,
                activityResponse: {
                  id: activityResponse.id,
                  status: activityResponse.status,
                  startDate: activityResponse.startDate,
                  endDate: activityResponse.endDate,
                  updatedBy: activityResponse.updatedBy,
                  updatedByName: activityResponse.updatedByName,
                  name: activityResponse.name,
                },
                allowEdit: _allowTaskEdit,
              };
            });
          }

          return activityResponse;
        });
        setGridData(formatedData);
      }
    } catch (error) {
    } finally {
      if (updateLoading) setLoading(false);
    }
  };

  const fetchGroupByPhasesData = async (
    updateLoading: boolean = true,
    filters: any = {}
  ) => {
    try {
      if (updateLoading) setLoading(true);

      // Prepare filter parameters for API call
      const filterParams = {
        activityIds: filters.activity ? filters.activity.join(",") : undefined,
        taskIds: filters.task ? filters.task.join(",") : undefined,
        phaseOwnerIds: filters.resourceHead
          ? filters.resourceHead.join(",")
          : undefined,
        taskOwnerIds: filters.assignee ? filters.assignee.join(",") : undefined,
        tagIds: filters.tag ? filters.tag.join(",") : undefined,
        completionRange: filters.completion || undefined,
        phaseStatus: filters.taskStatus || undefined,
        taskDateFilter: dateFilter !== "ALL" ? dateFilter : undefined, // Add date filter parameter
      };

      const apiResponse = await phaseApis.fetchGroupByPhasesList(
        {
          page: 1,
          pageSize: 100000,
          direction: "P",
          sortOrder: "ASC",
          pageDiff: 0,
          paginationId: null,
          projectIds: null,
          ...filterParams, // Spread the filter parameters
        },
        projectId
      );
      const { data, status } = apiResponse;
      if (status) {
        const { list, projectResponse } = data;
        const {
          status: projectStatus,
          isLocked,
          lockedBy,
        } = projectResponse || {};

        let _allowProjectEdit = true;
        if (
          ["closed", "onhold", "archived"]?.includes(
            projectStatus?.toLowerCase()
          )
        )
          _allowProjectEdit = false;
        if (isLocked && lockedBy !== currentUser?.id) {
          _allowProjectEdit = false;
        }
        setAllowAdd(_allowProjectEdit);
        const formattedData = list.map((phase: any) => {
          let _allowPhaseEdit = _allowProjectEdit;
          if (
            ["closed", "onhold", "archived"]?.includes(
              phase.status?.toLowerCase()
            )
          )
            _allowPhaseEdit = false;
          const phaseNode = {
            id: phase.phaseId,
            key: "P-" + phase.phaseId,
            name: phase.phaseName,
            owner: phase.phaseOwnerName,
            status: phase.status,
            startDate: phase.phaseStartDate,
            endDate: phase.phaseEndDate,
            duration: null,
            priority: null,
            completionPercentage: phase.activityCompletionPercentage,
            expanded: true,
            manuallyScheduled: true,
            cls: "phase-node",
            children: [],
            createdAt: phase.createdAt,
            plannedStartDate: phase.plannedStartDate,
            plannedEndDate: phase.plannedEndDate,
            projectResponse: { ...projectResponse, name: projectName },
            allowEdit: _allowPhaseEdit,
          };

          if (Array.isArray(phase.activities)) {
            const phaseResponse = {
              id: phase.phaseId,
              name: phase.phaseName,
              status: phase.status,
              startDate: phase.phaseStartDate,
              endDate: phase.phaseEndDate,
              owner: phase.phaseOwnerName,
            };
            phaseNode.children = phase.activities.map((activity: any) => {
              let _allowActivityEdit = _allowPhaseEdit;
              if (
                ["closed", "onhold", "archived"]?.includes(
                  activity.status?.toLowerCase()
                )
              )
                _allowActivityEdit = false;

              const activityResponse = {
                key: "A-" + activity.activityId,
                id: activity.activityId,
                name: activity.activityName,
                owner: activity.activityOwnerName,
                status: activity.status,
                updatedBy: activity.updatedBy || null,
                updatedByName: activity.updatedByName || "",
                startDate: activity.activityStartDate,
                endDate: activity.activityEndDate,
                duration: null,
                priority: null,
                completionPercentage: activity.tasksCompletionPercentage,
                expanded: true,
                manuallyScheduled: true,
                cls: "activity-node",
                children: [],
                projectResponse: { ...projectResponse, name: projectName },
                phaseResponse,
                allowEdit: _allowActivityEdit,
              };

              if (Array.isArray(activity.tasks)) {
                activityResponse.children = activity.tasks.map((task: any) => {
                  let _allowTaskEdit = _allowActivityEdit;
                  if (
                    ["closed", "onhold", "archived"]?.includes(
                      task.taskStatus?.toLowerCase()
                    )
                  )
                    _allowTaskEdit = false;
                  return {
                    key: "T-" + task.taskId,
                    id: task.taskId,
                    name: task.taskName,
                    owner: task.taskOwnerName,
                    status: task.taskStatus,
                    startDate: task.taskPlannedStartDate,
                    endDate: task.taskPlannedEndDate,
                    duration: task.taskDuration,
                    priority: task.priorityName,
                    completionPercentage: task.taskCompletionPercentage,
                    manuallyScheduled: true,
                    predecessors: task.predecessors,
                    successors: task.successors,
                    projectResponse: { ...projectResponse, name: projectName },
                    phaseResponse,
                    activityResponse: {
                      id: activity.activityId,
                      status: activityResponse.status,
                      startDate: activityResponse.startDate,
                      endDate: activityResponse.endDate,
                      updatedBy: activityResponse.updatedBy,
                      updatedByName: activityResponse.updatedByName,
                      name: activityResponse.name,
                    },
                    allowEdit: _allowTaskEdit,
                  };
                });
              }

              return activityResponse;
            });
          }

          return phaseNode;
        });

        setGridData(formattedData);
      }
    } catch (error) {
    } finally {
      if (updateLoading) setLoading(false);
    }
  };

  const fetchTaskDropDownData = async () => {
    try {
      const apiResponse = await tasksApis.fetchTaskDropDown({
        projectId,
        derivedProjectStatus: derivedProjectStatus,
      });
      const { data, status } = apiResponse;
      if (status) {
        let options = data?.map((item: any) => {
          return {
            label: item.taskName,
            value: item.taskId,
          };
        });
        setTaskDropdownData(options);
      }
    } catch (error) {}
  };

  const fetchTagFilterData = async () => {
    try {
      const response = await phaseApis.getTagsFilterData(projectId?.toString());
      const { data, status } = response || {};
      if (status) {
        const options = data.map((item: any) => {
          return {
            label: item.tagName,
            value: item.tagId,
          };
        });
        setTagFilterData(options);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  const fetchStatusFilterData = async () => {
    try {
      // Determine module based on current groupBy selection
      const module = groupBy === "phases" ? "Phase" : "Activity";
      const response = await projectsApis.fetchStatusFiltersByModule(module);
      const { data, status } = response;
      if (status) {
        const options = data.map((item: any) => {
          return {
            label: item,
            value: item,
          };
        });
        setStatusFilterData(options);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  const fetchResourceHeadFiltersList = async () => {
    try {
      let apiResponse;

      if (groupBy === "phases") {
        // Call phase/filters/owners API
        apiResponse = await phaseApis.getOwnersFilterData(projectId);
      } else {
        // Call activity/filters/owners API (default for taskList)
        apiResponse = await activityApis.getOwnersFilterData(projectId, null);
      }

      const { data, status } = apiResponse;
      if (status) {
        const options = data.map((item: any) => {
          return {
            label: item.userName,
            value: item.userId,
          };
        });
        setresourceHeadFiltersList(options);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  const fetchActivityFilterData = async (setFormValueAfterLoad = false) => {
    try {
      if (projectId) {
        const response = await documentApis.fetchActivityDropDown({
          projectId,
        });
        const activities = response?.data || response || [];
        const options = activities.map((item: any) => ({
          label: item.activityName,
          value: item.activityId.toString(), // Ensure value is string
        }));
        setActivityDropdownData(options);

        // Set form value after options are loaded (for initial filter from navigation)
        if (setFormValueAfterLoad && filterActivityId) {
          methods.setValue("activity", [filterActivityId.toString()]);
        }
      }
    } catch (error) {
      console.error("Error loading activity data:", error);
    }
  };

  const fetchAssigneeFilterData = async () => {
    try {
      if (projectId) {
        const response = await tasksApis.getOwnersFilterData(
          projectId?.toString()
        );
        const { data, status } = response || {};
        if (status) {
          const options = data.map((item: any) => ({
            label: item.userName,
            value: item.userId,
          }));
          setAssigneeFilterData(options);
        }
      }
    } catch (error) {
      console.error("Error loading assignee data:", error);
    }
  };

  useEffect(() => {
    fetchAssigneeFilterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Fetch activities (with phase info) for the Add Task activity selector via
  // the lightweight /activity/dropdown endpoint. Preloaded on projectId change
  // so the Add Task button can resolve phase info from a single applied
  // activity filter without waiting for the selector modal to open.
  useEffect(() => {
    if (projectId) {
      documentApis.fetchActivityDropDown({ projectId }).then((res: any) => {
        const activities = res?.data ?? res ?? [];
        if (Array.isArray(activities)) {
          const options = activities.map((item: any) => ({
            label: item.phaseName
              ? `${item.activityName} (${item.phaseName})`
              : item.activityName,
            value: String(item.activityId),
            activityName: item.activityName,
            phaseId: item.phaseId,
            phaseName: item.phaseName,
          }));
          setActivityOptionsWithPhase(options);
        }
      });
    }
  }, [projectId]);

  useEffect(() => {
    if (filtersModal) {
      fetchTagFilterData();
      fetchStatusFilterData();
      fetchResourceHeadFiltersList();
      fetchActivityFilterData();
      fetchAssigneeFilterData();
    }
  }, [filtersModal, groupBy]);

  // Handle subsequent navigations with activity filter (e.g., clicking different activity)
  const prevFilterActivityIdRef = React.useRef(filterActivityId);
  useEffect(() => {
    // Only update if filterActivityId changed (not on initial mount)
    if (prevFilterActivityIdRef.current !== filterActivityId) {
      if (filterActivityId && filterActivityName) {
        setAppliedFilters({ activity: [filterActivityId.toString()] });
        setActiveActivityFilter({ id: filterActivityId, name: filterActivityName });
        setFilterCount(1);
        setGroupBy("taskList");
        // Also update the form field (use string to match dropdown option values)
        methods.setValue("activity", [filterActivityId.toString()]);
      } else {
        // Clear filter if navigating without activity filter
        setAppliedFilters({});
        setActiveActivityFilter(null);
        setFilterCount(0);
        methods.setValue("activity", []);
      }
      prevFilterActivityIdRef.current = filterActivityId;
    }
  }, [filterActivityId, filterActivityName]);

  // Handle navigations with a status filter (e.g. clicking a status row in the
  // dashboard's Task Summary). Seed the form, applied filters and group-by so
  // the user lands on a flat task list filtered to the chosen status.
  const prevFilterStatusRef = React.useRef(filterStatus);
  useEffect(() => {
    // Initial mount: just seed the form value if filterStatus is present.
    if (filterStatus) {
      methods.setValue("taskStatus", filterStatus);
      setGroupBy("taskList");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (prevFilterStatusRef.current !== filterStatus) {
      if (filterStatus) {
        setAppliedFilters((prev: any) => ({
          ...prev,
          taskStatus: filterStatus,
        }));
        setFilterCount((c) => c + 1);
        setGroupBy("taskList");
        methods.setValue("taskStatus", filterStatus);
      } else {
        setAppliedFilters((prev: any) => {
          const { taskStatus: _drop, ...rest } = prev || {};
          return rest;
        });
        setFilterCount((c) => Math.max(0, c - 1));
        methods.setValue("taskStatus", undefined);
      }
      prevFilterStatusRef.current = filterStatus;
    }
  }, [filterStatus]);

  // Status filter options are normally loaded only when the filter modal opens,
  // but if we arrived with a pre-applied status filter we want the option list
  // available so the chip / select shows the label correctly.
  useEffect(() => {
    if (filterStatus && statusFilterData.length === 0) {
      fetchStatusFilterData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus]);

  // Fetch activity dropdown data and set form value on initial mount if activity filter exists
  useEffect(() => {
    if (filterActivityId) {
      // Fetch activity dropdown data so the label can be displayed
      // Pass true to set form value after options are loaded
      fetchActivityFilterData(true);
    }
  }, []);

  useEffect(() => {
    // Gantt view fetches its own data via GanttContext — skip the
    // table-view fetch to avoid a duplicate, slower request.
    if (listViewType !== "table") return;
    if (groupBy === "taskList") {
      fetchGroupByActivityListData(true, appliedFilters);
    } else if (groupBy === "phases") {
      fetchGroupByPhasesData(true, appliedFilters);
    }
    fetchTaskDropDownData();
  }, [groupBy, appliedFilters, dateFilter, listViewType]);

  const refresh = (
    updateLoading: boolean = false,
    filters: any = appliedFilters
  ) => {
    if (groupBy === "taskList") {
      fetchGroupByActivityListData(updateLoading, filters);
    } else if (groupBy === "phases") {
      fetchGroupByPhasesData(updateLoading, filters);
    }
    fetchTaskDropDownData();
  };

  // ---- Stats strip: counts over TASK nodes ("T-" keys) in the loaded tree ----
  const taskStats = useMemo(() => {
    const tasks: any[] = [];
    const walk = (nodes: any[]) =>
      (nodes || []).forEach((n: any) => {
        if (String(n?.key || "").startsWith("T-")) tasks.push(n);
        if (Array.isArray(n?.children)) walk(n.children);
      });
    walk(GridData);
    return {
      total: tasks.length,
      inProgress: tasks.filter(STAT_PREDICATES.inprogress).length,
      completed: tasks.filter(STAT_PREDICATES.completed).length,
      overdue: tasks.filter(STAT_PREDICATES.overdue).length,
      onHold: tasks.filter(STAT_PREDICATES.onhold).length,
    };
  }, [GridData]);

  const toggleStatFilter = (key: TaskStatKey) =>
    setStatFilter((prev) => (prev === key ? null : key));

  // Table-view data after the client-side stat filter + name search. The stat
  // filter keeps only matching TASK nodes (and ancestors that still have
  // matching descendants); search keeps a node when its name matches or any
  // descendant matches. GridData itself is never mutated.
  const displayGridData = useMemo(() => {
    let result: any[] = GridData;

    if (statFilter) {
      const predicate = STAT_PREDICATES[statFilter];
      const applyStat = (nodes: any[]): any[] =>
        (nodes || []).reduce((acc: any[], n: any) => {
          if (String(n?.key || "").startsWith("T-")) {
            if (predicate(n)) acc.push(n);
          } else {
            const children = applyStat(n?.children || []);
            if (children.length) acc.push({ ...n, children });
          }
          return acc;
        }, []);
      result = applyStat(result);
    }

    const q = treeSearch.trim().toLowerCase();
    if (q) {
      const applySearch = (nodes: any[]): any[] =>
        (nodes || []).reduce((acc: any[], n: any) => {
          const selfMatches = String(n?.name || "")
            .toLowerCase()
            .includes(q);
          if (selfMatches) {
            // Node itself matches — keep its whole subtree.
            acc.push(n);
            return acc;
          }
          const children = Array.isArray(n?.children)
            ? applySearch(n.children)
            : [];
          if (children.length) acc.push({ ...n, children });
          return acc;
        }, []);
      result = applySearch(result);
    }

    return result;
  }, [GridData, statFilter, treeSearch]);

  // Assignee filter: applied immediately on change through the same
  // appliedFilters path the filter drawer uses — the fetch effect re-runs on
  // appliedFilters change and sends taskOwnerIds to the API.
  const handleAssigneeChange = (values: any[]) => {
    const next: any = {
      ...appliedFilters,
      assignee: Array.isArray(values) && values.length > 0 ? values : undefined,
    };
    const filtered = Object.keys(next).reduce((acc: any, key) => {
      const val = next[key];
      if (
        val !== null &&
        val !== undefined &&
        val !== "" &&
        (!Array.isArray(val) || val.length > 0)
      ) {
        acc[key] = val;
      }
      return acc;
    }, {});
    setAppliedFilters(filtered);
    // Assignee is a toolbar-level filter — keep it out of the drawer badge
    // count, same as the old apply-on-close dropdown did.
    setFilterCount(Object.keys(filtered).filter((k) => k !== "assignee").length);
  };

  useEffect(() => {
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => {
          return {
            ...item,
            linkState: location.state,
          };
        })
      )
    );
  }, [location]);
  // Breadcrumb mirrors the old "{project} / {phase} / {activity} / Tasks"
  // header, including any active phase/activity filter crumbs.
  const breadcrumb = [
    { label: projectName },
    ...(filterPhaseName ? [{ label: wbsName(filterPhaseName) }] : []),
    ...(activeActivityFilter
      ? [{ label: wbsName(activeActivityFilter.name) }]
      : []),
    { label: "Tasks" },
  ];

  const handleBack = () =>
    navigate(`/pms/project/dashboard/${projectId}`, {
      state: {
        ...(location.state || {}),
        id: projectId,
        projectName,
        fromDashboard: undefined,
      },
    });

  const moreMenuItems: MenuProps["items"] = [
    {
      key: "wbs-import",
      icon: <Icon name="upload-cloud" />,
      label: "Upload WBS",
    },
    {
      key: "task-budget",
      icon: <Icon name="upload-cloud" />,
      label: "Upload Budgets",
    },
    {
      key: "apply-norm",
      icon: <Icon name="upload-cloud" />,
      label: "Apply Norm (Material)",
    },
    {
      key: "apply-manpower",
      icon: <Icon name="upload-cloud" />,
      label: "Apply Norm (Manpower - PRW)",
    },
    {
      key: "apply-machinery",
      icon: <Icon name="upload-cloud" />,
      // Covers machinery norms AND deployment norms (Equipment) — the modal's
      // Head picker switches between the two flows.
      label: "Apply Norm (Machinery)",
    },
    {
      key: "task-schedule",
      icon: <Icon name="upload-cloud" />,
      label: "Upload Monthly Schedule",
    },
    {
      key: "task-actual",
      icon: <Icon name="upload-cloud" />,
      label: "Upload Monthly Actuals",
    },
    {
      key: "task-resource",
      icon: <Icon name="upload-cloud" />,
      label: "Upload Resources",
    },
    {
      key: "task-tool",
      icon: <Icon name="upload-cloud" />,
      label: "Upload Tools",
    },
    { type: "divider" },
    {
      key: "export-wbs",
      icon: <Icon name="download-cloud" />,
      label: "Export WBS",
    },
  ];

  const handleMoreMenuClick: MenuProps["onClick"] = ({ key }) => {
    if (key === "apply-norm") {
      setApplyNormUploadOpen(true);
      return;
    }
    if (key === "apply-manpower") {
      setApplyManpowerUploadOpen(true);
      return;
    }
    if (key === "apply-machinery") {
      setApplyMachineryUploadOpen(true);
      return;
    }
    if (key === "task-schedule") {
      setScheduleUploadOpen(true);
      return;
    }
    if (key === "task-actual") {
      setActualUploadOpen(true);
      return;
    }
    if (key === "export-wbs") {
      // Trigger the hidden Export component's button (kept mounted in the
      // toolbar below).
      setTimeout(() => {
        const exportBtn = document.querySelector(
          ".btn.btn-white.btn-outline-light"
        );
        if (exportBtn) {
          (exportBtn as HTMLElement).click();
        }
      }, 100);
      return;
    }
    handleBulkUploadMenuClick(key);
  };

  const statsStrip = (
    <>
      <StatCard
        label="Total Tasks"
        value={taskStats.total}
        tone="brand"
        icon={<UnorderedListOutlined />}
        loading={loading}
      />
      <StatCard
        label="In Progress"
        value={taskStats.inProgress}
        tone="In Progress"
        icon={<SyncOutlined />}
        loading={loading}
        onClick={() => toggleStatFilter("inprogress")}
        active={statFilter === "inprogress"}
      />
      <StatCard
        label="Completed"
        value={taskStats.completed}
        tone="Completed"
        icon={<CheckCircleOutlined />}
        loading={loading}
        onClick={() => toggleStatFilter("completed")}
        active={statFilter === "completed"}
      />
      <StatCard
        label="Overdue"
        value={taskStats.overdue}
        tone="Overdue"
        icon={<ExclamationCircleOutlined />}
        loading={loading}
        onClick={() => toggleStatFilter("overdue")}
        active={statFilter === "overdue"}
      />
      <StatCard
        label="On Hold"
        value={taskStats.onHold}
        tone="On Hold"
        icon={<PauseCircleOutlined />}
        loading={loading}
        onClick={() => toggleStatFilter("onhold")}
        active={statFilter === "onhold"}
      />
    </>
  );

  // ---- Excel export: the visible tasks with their full 3M breakdown ----
  // Scope is the filtered tree, not the project: the page's assignee / date /
  // status / tag / stat / search filters exist only here (the 3M query layer
  // filters by phase, activity and task alone), so the task IDs on screen are
  // what defines the export.
  const [exporting, setExporting] = useState(false);

  const exportFiltersText = () => {
    const parts: string[] = [];
    if (filterPhaseName) parts.push(`Phase: ${wbsName(filterPhaseName)}`);
    if (activeActivityFilter)
      parts.push(`Activity: ${wbsName(activeActivityFilter.name)}`);
    if (groupBy !== "phases") parts.push("Grouped by activity");
    if (dateFilter && dateFilter !== "ALL") {
      const label = dateFilterOptions.find((o: any) => o.key === dateFilter)
        ?.label;
      if (label) parts.push(`Dates: ${label}`);
    }
    const assignees = appliedFilters?.assignee;
    if (Array.isArray(assignees) && assignees.length)
      parts.push(`Assignees: ${assignees.length}`);
    if (appliedFilters?.taskStatus) parts.push(`Status: ${appliedFilters.taskStatus}`);
    if (Array.isArray(appliedFilters?.tag) && appliedFilters.tag.length)
      parts.push(`Tags: ${appliedFilters.tag.length}`);
    if (statFilter) parts.push(`Card filter: ${statFilter}`);
    if (treeSearch.trim()) parts.push(`Search: "${treeSearch.trim()}"`);
    return parts.join(" · ") || "None (full project)";
  };

  const handleExport = async () => {
    if (!projectId) return;
    // Task IDs of the tree AFTER every filter — displayGridData is the same
    // data the table renders.
    const visibleTaskIds = new Set<number>();
    const collect = (nodes: any[]) =>
      (nodes || []).forEach((n: any) => {
        if (String(n?.key || "").startsWith("T-")) visibleTaskIds.add(Number(n.id));
        if (Array.isArray(n?.children)) collect(n.children);
      });
    collect(displayGridData);
    if (!visibleTaskIds.size) {
      message.warning("No tasks to export for the current filters");
      return;
    }

    setExporting(true);
    try {
      // phaseId/activityId only narrow the payload server-side; everything
      // else is intersected with visibleTaskIds in the builder.
      const scope: any = {};
      if (filterPhaseId) scope.phaseId = filterPhaseId;
      if (activeActivityFilter?.id) scope.activityId = activeActivityFilter.id;

      const [treeRes, materialRes, manpowerRes, machineryRes, helpers] =
        await Promise.all([
          threeMReportApis.get3MConsolidation(projectId, scope),
          threeMReportApis.get3MMaterialTaskBreakdownAll(projectId, scope),
          threeMReportApis.get3MManpowerTaskBreakdownAll(projectId, scope),
          // No `head` — one payload with MUCKING + TOOLS + EQUIPMENT rows.
          threeMReportApis.get3MMachineryTaskBreakdownAll(projectId, scope),
          import("../reports/shared/excelHelpers"),
        ]);

      if (!treeRes?.status) {
        const msg = String(treeRes?.message || "");
        message.error(
          /permission|access|forbidden/i.test(msg)
            ? "You don't have access to 3M cost data — ask your admin for the 3M Consolidation Report module"
            : msg || "Failed to load the 3M data for export"
        );
        return;
      }
      // The pillar registers are additive: a project with none of a given
      // resource still gets the rest of the workbook.
      [materialRes, manpowerRes, machineryRes].forEach((res: any) => {
        if (!res?.status)
          console.warn("3M breakdown fetch skipped:", res?.message);
      });

      const logo = await helpers.fetchLogoBase64(currentUser?.orgLogo);
      const { default: exportTasks3MWorkbook } = await import(
        "./exportTasks3MWorkbook"
      );
      await exportTasks3MWorkbook({
        tree: treeRes.data || {},
        materialRows: materialRes?.status ? materialRes.data?.rows || [] : [],
        manpowerRows: manpowerRes?.status ? manpowerRes.data?.rows || [] : [],
        machineryRows: machineryRes?.status ? machineryRes.data?.rows || [] : [],
        visibleTaskIds,
        projectName,
        orgName: currentUser?.orgName,
        logo,
        filtersText: exportFiltersText(),
        taskCounts: { visible: visibleTaskIds.size, total: taskStats.total },
      });
      message.success("Excel exported");
    } catch (err) {
      console.error("Error exporting the tasks 3M register:", err);
      message.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const toolbar = (
    <>
      <Segmented
        value={listViewType}
        onChange={(value) => setListViewType(value as string)}
        options={[
          { value: "table", icon: <TableOutlined />, label: "Table" },
          { value: "gantt", icon: <BarChartOutlined />, label: "Gantt" },
          { value: "Kanban", icon: <AppstoreOutlined />, label: "Kanban" },
        ]}
      />
      {listViewType === "table" && (
        <>
          <Input
            allowClear
            prefix={<SearchOutlined style={{ color: "#8094ae" }} />}
            placeholder="Search tasks"
            style={{ width: 240 }}
            value={treeSearch}
            onChange={(e) => setTreeSearch(e.target.value)}
          />
          <Tooltip
            title={expandedMode === "all" ? "Collapse to phases" : "Expand all"}
          >
            <AntButton
              icon={
                expandedMode === "all" ? (
                  <ShrinkOutlined />
                ) : (
                  <ArrowsAltOutlined />
                )
              }
              onClick={() =>
                setExpandedMode((m) => (m === "all" ? "phases" : "all"))
              }
            />
          </Tooltip>
          <WbsCodeToggle />
        </>
      )}
      <Select
        value={groupBy}
        onChange={(value) => setGroupBy(value)}
        options={groupByOptions.map((item: any) => ({
          value: item.key,
          label: item.label,
        }))}
        style={{ minWidth: 170 }}
      />
      {listViewType === "table" && (
        <>
          <Select
            value={dateFilter}
            onChange={(value) => setDateFilter(value)}
            options={dateFilterOptions.map((item: any) => ({
              value: item.key,
              label: item.label,
            }))}
            style={{ minWidth: 140 }}
          />
          <Select
            mode="multiple"
            maxTagCount={1}
            allowClear
            placeholder="Assignees"
            style={{ minWidth: 180 }}
            value={appliedFilters.assignee ?? []}
            onChange={handleAssigneeChange}
            options={assigneeFilterData}
            optionFilterProp="label"
          />
        </>
      )}
      <span className="pms-toolbar-spacer" />
      <AddAccess moduleName="Tasks">
        <AntButton
          type="primary"
          icon={<PlusOutlined />}
          disabled={!allowAdd}
          onClick={() => {
            // Resolve the activity to use without prompting:
            // 1) Explicit location-state filter (came from another page)
            // 2) A single activity currently selected in the filter drawer
            // Otherwise, always prompt via the selector modal.
            let resolved:
              | {
                  activityId: number;
                  activityName: string;
                  phaseId: number;
                  phaseName: string;
                }
              | null = null;

            const appliedActivity = appliedFilters?.activity;
            if (
              filterActivityId &&
              filterActivityName &&
              filterPhaseId &&
              filterPhaseName &&
              (!Array.isArray(appliedActivity) ||
                appliedActivity.length === 0 ||
                appliedActivity
                  .map((v: any) => String(v))
                  .includes(String(filterActivityId)))
            ) {
              resolved = {
                activityId: filterActivityId,
                activityName: filterActivityName,
                phaseId: filterPhaseId,
                phaseName: filterPhaseName,
              };
            } else if (
              Array.isArray(appliedActivity) &&
              appliedActivity.length === 1
            ) {
              const opt = activityOptionsWithPhase.find(
                (o) => o.value === String(appliedActivity[0])
              );
              if (opt && opt.phaseId) {
                resolved = {
                  activityId: Number(opt.value),
                  activityName: opt.activityName,
                  phaseId: opt.phaseId,
                  phaseName: opt.phaseName,
                };
              }
            }

            // Full-page 4-step create flow; activity (when unresolved) is
            // picked inline on step 1 of the page.
            navigate(`/pms/project/task-create/${projectId}`, {
              state: {
                id: projectId,
                projectName,
                preselectedActivity: resolved ?? undefined,
              },
            });
          }}
        >
          Add Task
        </AntButton>
      </AddAccess>
      <Dropdown
        menu={{ items: moreMenuItems, onClick: handleMoreMenuClick }}
        trigger={["click"]}
      >
        <AntButton type="text" icon={<MoreOutlined />} />
      </Dropdown>
      <div style={{ display: "none" }}>
        <div id="hidden-export-btn">
          <Export
            data={{
              sortOrder: "DESC",
              pageSize: 100,
              direction: "P",
              pageDiff: 0,
              projectIds: projectId ? projectId.toString() : undefined,
            }}
            moduleKey="project/tasks"
            showAsButton={true}
          />
        </div>
      </div>
      <Tooltip title="Export the visible tasks with their Material / Manpower / Mucking / Machinery breakdown">
        <AntButton
          type="text"
          icon={<FileExcelOutlined />}
          loading={exporting}
          disabled={!projectId || loading || taskStats.total === 0}
          onClick={handleExport}
        />
      </Tooltip>
      <AntButton
        type="text"
        icon={<ReloadOutlined />}
        title="Refresh"
        onClick={() => refresh(true)}
      />
      {listViewType !== "Kanban" && (
        <Badge color="geekblue" size="small" count={filterCount}>
          <AntButton
            type="text"
            icon={<FilterOutlined />}
            onClick={() => setFiltersModal(true)}
          />
        </Badge>
      )}
    </>
  );

  // NOTE: all modals/drawers are mounted as SIBLINGS of <ProjectPageShell> —
  // the loading swap inside the shell's children must never unmount the
  // staged upload modals mid-flow.
  return (
    <>
      <ProjectPageShell
          headTitle="Tasks"
          breadcrumb={breadcrumb}
          onBack={fromDashboard ? handleBack : undefined}
          actions={
            activeActivityFilter ? (
              <Tag
                color="blue"
                closable
                onClose={() => {
                  setActiveActivityFilter(null);
                  setAppliedFilters({});
                  methods.reset();
                  setFilterCount(0);
                  // Navigate to clear the location state
                  navigate(location.pathname, {
                    state: {
                      ...location.state,
                      filterActivityId: undefined,
                      filterActivityName: undefined,
                      filterPhaseId: undefined,
                      filterPhaseName: undefined,
                    },
                    replace: true,
                  });
                }}
                style={{
                  fontSize: 13,
                  padding: "4px 10px",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                Clear Filter
              </Tag>
            ) : undefined
          }
          stats={statsStrip}
          toolbar={toolbar}
          stickyToolbar={false}
        >
          {/* White surface behind the table/gantt/kanban (matches hub + view pages) */}
          <div className="pms-hub-surface">
            {loading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "80px 0",
                }}
              >
                <Spin indicator={<LoadingOutlined spin />} size="large" />
              </div>
            ) : (
              <>
                {listViewType === "gantt" && (
                  <GanttChart projectId={projectId} groupBy={groupBy} />
                )}
                {listViewType === "table" &&
                  (displayGridData.length > 0 ? (
                    <GridView
                      projectId={projectId}
                      gridData={displayGridData}
                      taskDropdownData={taskDropdownData}
                      refresh={refresh}
                      groupBy={groupBy}
                      expandedMode={expandedMode}
                      forceExpandAll={Boolean(treeSearch.trim()) || Boolean(statFilter)}
                    />
                  ) : (
                    <div style={{ padding: "48px 0", textAlign: "center" }}>
                      <Empty description="No Data Found" />
                    </div>
                  ))}
                {listViewType === "Kanban" && <KanbanView groupBy={groupBy} />}
              </>
            )}
          </div>
      </ProjectPageShell>
      <Drawer
        maskClosable={false}
        title={
          <div className="d-flex justify-content-between align-items-center">
            <strong>Filters</strong>
            <>
              <div style={{ textAlign: "right" }}>
                <AntButton
                  type="link"
                  size="small"
                  onClick={handleResetFilters}
                >
                  Reset
                </AntButton>
              </div>
            </>
          </div>
        }
        placement="right"
        width={450}
        onClose={() => {
          setFiltersModal(false);
        }}
        open={filtersModal}
        styles={{ body: { paddingBottom: 80 } }}
      >
        <div className="d-flex align-items-center flex-wrap w-100">
          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit(handleFilterSubmit)}
              className="w-100"
            >
              <div key={"filters"} className="mb-2 mt-0">
                <FormMultiSelect
                  name="activity"
                  label={"Activity"}
                  placeholder={"filter By Activity"}
                  options={activityFilterOptions}
                />
              </div>
              <div key={"filters2"} className="mb-2 mt-0">
                <FormMultiSelect
                  name="task"
                  label={"Task"}
                  placeholder={"filter By Task"}
                  options={taskDropdownData}
                />
              </div>
              <div key={"filters"} className="mb-2 mt-0">
                <FormMultiSelect
                  name="resourceHead"
                  label={"Resource Head"}
                  placeholder={"By Resource Head"}
                  options={resourceHeadFiltersList}
                />
              </div>

              <div key={"filters2"} className="mt-0 mb-2">
                <FormMultiSelect
                  name="tag"
                  label={"Tag"}
                  placeholder={"By Tag"}
                  options={tagFilterData}
                />
              </div>

              <div key={"filters3"} className="mt-0 mb-2">
                <FormSelect
                  name="completion"
                  label={"Completion %"}
                  placeholder={"By Completion %"}
                  options={Array.from({ length: 20 }, (_, i) => {
                    const start = i * 5 + 1;
                    const end = (i + 1) * 5;
                    return {
                      value: `${start}-${end}`,
                      label: `${start}–${end}%`,
                    };
                  })}
                />
              </div>

              <div key={"filters4"} className="mt-0 mb-2">
                <FormSelect
                  name="taskStatus"
                  label={"Status"}
                  placeholder={"By Status"}
                  options={statusFilterData}
                />
              </div>

              <Space style={{ marginTop: 4 }}>
                <AntButton type="primary" size="small" htmlType="submit">
                  Find
                </AntButton>
                <AntButton
                  size="small"
                  onClick={() => {
                    methods.reset();
                    setFiltersModal(false);
                    setAppliedFilters({});
                    setFilterCount(0);
                  }}
                >
                  Cancel
                </AntButton>
              </Space>
            </form>
          </FormProvider>
        </div>
      </Drawer>
      {/* Staged task-level upload modal (task-budget/boq/resource/tool) */}
      <TaskTypeUploadModal
        uploadKey={taskUploadKey}
        projectId={projectId}
        projectName={projectName}
        projectSiteName={projectSiteName}
        projectSiteId={projectSiteId}
        open={taskUploadOpen}
        onClose={() => setTaskUploadOpen(false)}
        onSuccess={() => refresh(false)}
      />
      {/* Staged "Apply Norm" upload (rows: TaskID, NormCode).
          onSuccess uses a SILENT refresh (updateLoading=false): refresh(true)
          flips `loading`, which hits the `if (loading) return <Spinner>` early
          return and unmounts this modal mid-"done" — remounting it back to the
          upload stage. Norm-apply doesn't change the task list anyway. */}
      <ApplyNormUploadModal
        projectId={projectId}
        open={applyNormUploadOpen}
        onClose={() => setApplyNormUploadOpen(false)}
        onSuccess={() => refresh(false)}
      />
      <ApplyManpowerNormUploadModal
        projectId={projectId}
        open={applyManpowerUploadOpen}
        onClose={() => setApplyManpowerUploadOpen(false)}
        onSuccess={() => refresh(false)}
      />
      {/* One modal for machinery norms AND deployment norms (Equipment) —
          its Head picker switches the flow. Silent refresh for the same
          unmount reason as above. */}
      <ApplyMachineryNormUploadModal
        projectId={projectId}
        open={applyMachineryUploadOpen}
        onClose={() => setApplyMachineryUploadOpen(false)}
        onSuccess={() => refresh(false)}
      />
      {/* Staged "Monthly Schedule" upload (rows: Task ID, Month, Scheduled
          Quantity). Silent refresh for the same unmount reason as above —
          schedules don't change the task list either. */}
      <TaskScheduleUploadModal
        projectId={projectId}
        open={scheduleUploadOpen}
        onClose={() => setScheduleUploadOpen(false)}
        onSuccess={() => refresh(false)}
      />
      {/* Staged "Monthly Actuals" upload (rows: Task ID, Month, Actual Qty,
          Remarks). Month-scoped replace; silent refresh like the schedule. */}
      <TaskActualUploadModal
        projectId={projectId}
        open={actualUploadOpen}
        onClose={() => setActualUploadOpen(false)}
        onSuccess={() => refresh(false)}
      />
      {/* Staged WBS upload modal (upload → validate → preview → create).
          onSuccess uses a SILENT refresh (updateLoading=false): refresh(true)
          flips `loading`, which hits the `if (loading) return <Spinner>` early
          return and unmounts this modal mid-"done" — remounting it back to the
          upload stage. A silent refresh still repopulates the grid in the
          background while the Done screen stays visible. */}
      <WbsUploadModal
        projectId={projectId}
        projectName={projectName}
        projectSiteName={projectSiteName}
        projectSiteId={projectSiteId}
        open={wbsUploadOpen}
        onClose={() => setWbsUploadOpen(false)}
        onSuccess={() => refresh(false)}
      />
    </>
  );
};

export default TasksListView;
